// Signage → Pantallas (/signage/pantallas). Alta/edición de pantallas, con el
// token/URL del reproductor (para abrir en el box Android en kiosko), estado
// online/offline (último latido) y regeneración de token.
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BatteryCharging, Camera, Copy, Eraser, ExternalLink, Link2, Monitor, Pencil, Plus, Power, QrCode, RefreshCcw, RefreshCw, RotateCw, Trash2, Wifi } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Modal, Switch, fieldCls, locationsList } from '@/features/pos/shared';
import type { LocationOpt } from '@/features/pos/shared';
import { GADGETS_DEFAULT, GadgetsEditor } from './GadgetsEditor';
import type { GadgetsCfg } from './player';
import { LOCALE } from '@/lib/utils/regional';

const SIG = { headers: { 'Accept-Profile': 'signage', 'Content-Profile': 'signage' } };

interface Pantalla {
  id: string; codigo: string | null; nombre: string; token: string;
  location_id: string | null; grupo_id: string | null; plantilla_id: string | null; tema_id: string | null;
  orientacion: string; resolucion: string | null; es_activo: boolean;
  last_seen: string | null; version: string | null;
  gadgets: GadgetsCfg | null;   // null = hereda del grupo
  modo: string; caja_id: string | null;   // 'caja' = pantalla de cliente atada a una caja del POS
  // salud del box (último latido, script 418) — kiosk=true si corre Fully Kiosk
  salud: { kiosk?: boolean; bateria?: number; enchufado?: boolean; wifi?: number; modelo?: string; app?: string; heap_mb?: number; uptime_min?: number } | null;
}
interface Ref { id: string; nombre: string }

const urlReproductor = (token: string) =>
  `${window.location.origin}${import.meta.env.BASE_URL}signage/tv/${token}`;
const online = (last: string | null) => !!last && Date.now() - new Date(last).getTime() < 120000;

export function Pantallas() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [edit, setEdit] = useState<Partial<Pantalla> | null>(null);
  const [vinc, setVinc] = useState<string | null>(null); // código de emparejamiento (null = alta normal)
  const [guardando, setGuardando] = useState(false);
  const cerrar = () => { setEdit(null); setVinc(null); };

  const rest = <T,>(q: string) => apiClient.get<T[]>(`/rest/signage.${q}`, SIG).then((r) => r.data);
  const { data: pantallas = [] } = useQuery<Pantalla[]>({
    queryKey: ['sig-pantallas'], staleTime: 0, refetchInterval: 30000,
    queryFn: () => rest<Pantalla>('pantalla?select=*&deleted_at=is.null&order=nombre'),
  });
  const { data: locations = [] } = useQuery<LocationOpt[]>({ queryKey: ['pos-locations'], queryFn: locationsList });
  const { data: grupos = [] } = useQuery<Ref[]>({ queryKey: ['sig-grupos'], queryFn: () => rest<Ref>('grupo?select=id,nombre&deleted_at=is.null&order=nombre') });
  const { data: plantillas = [] } = useQuery<Ref[]>({ queryKey: ['sig-plantillas'], queryFn: () => rest<Ref>('plantilla?select=id,nombre&deleted_at=is.null&order=nombre') });
  const { data: temas = [] } = useQuery<Ref[]>({ queryKey: ['sig-temas'], queryFn: () => rest<Ref>('marca_tema?select=id,nombre&deleted_at=is.null&order=nombre') });
  // Cajas del POS (para el modo "pantalla de cliente en caja")
  const { data: cajas = [] } = useQuery<{ id: string; nombre: string; location_id: string | null }[]>({
    queryKey: ['sig-cajas'],
    queryFn: async () => (await apiClient.get<{ id: string; nombre: string; location_id: string | null }[]>(
      '/rest/pos.cajas?select=id,nombre,location_id&deleted_at=is.null&order=nombre', { headers: { 'Accept-Profile': 'pos' } })).data,
    staleTime: 5 * 60 * 1000,
  });

  const nombreLoc = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l.nombre])), [locations]);
  const nombrePla = useMemo(() => Object.fromEntries(plantillas.map((p) => [p.id, p.nombre])), [plantillas]);

  const invalidar = () => qc.invalidateQueries({ queryKey: ['sig-pantallas'] });

  const guardar = async () => {
    if (!edit?.nombre) { toast.error('El nombre es obligatorio'); return; }
    if (vinc !== null && !vinc.trim()) { toast.error('Ingresa el código que muestra la pantalla'); return; }
    setGuardando(true);
    try {
      if (vinc !== null) {
        await apiClient.post('/rpc/sig_emparejar_confirmar', { _codigo: vinc.trim(), _data: {
          nombre: edit.nombre, codigo_pantalla: edit.codigo || null,
          location_id: edit.location_id || null, grupo_id: edit.grupo_id || null,
          plantilla_id: edit.plantilla_id || null, tema_id: edit.tema_id || null,
          orientacion: edit.orientacion || 'horizontal',
        } }, SIG);
        toast.success('Pantalla vinculada — el reproductor arranca solo');
      } else {
        const body = {
          nombre: edit.nombre, codigo: edit.codigo || null,
          location_id: edit.location_id || null, grupo_id: edit.grupo_id || null,
          plantilla_id: edit.plantilla_id || null, tema_id: edit.tema_id || null,
          orientacion: edit.orientacion || 'horizontal', resolucion: edit.resolucion || null,
          es_activo: edit.es_activo ?? true,
          gadgets: edit.gadgets ?? null,   // null = hereda del grupo
          modo: edit.modo || 'playlist',
          caja_id: (edit.modo === 'caja' ? edit.caja_id : null) || null,
        };
        if (edit.id) await apiClient.patch(`/rest/signage.pantalla?id=eq.${edit.id}`, body, SIG);
        else await apiClient.post('/rest/signage.pantalla', body, SIG);
        toast.success('Pantalla guardada');
      }
      cerrar(); invalidar();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setGuardando(false); }
  };

  const comando = async (p: Pantalla, tipo: string, label: string) => {
    try { await apiClient.post('/rpc/sig_comando_enviar', { _pantalla: p.id, _tipo: tipo }, SIG); toast.success(`${label} enviado a ${p.nombre}`); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };

  // Captura de pantalla remota (Fully Kiosk): encola el comando y sondea el
  // resultado — el box revisa su cola cada 15 s, así que tarda ~15-25 s.
  const [captura, setCaptura] = useState<{ pantalla: string; img?: string; msg?: string; esperando: boolean } | null>(null);
  const [qrDe, setQrDe] = useState<Pantalla | null>(null);   // QR de la URL del reproductor (escanear y abrir)
  const capturar = async (p: Pantalla) => {
    setCaptura({ pantalla: p.nombre, esperando: true });
    try {
      const r = (await apiClient.post('/rpc/sig_comando_enviar', { _pantalla: p.id, _tipo: 'captura' }, SIG)).data as { id: string };
      const t0 = Date.now();
      for (;;) {
        if (Date.now() - t0 > 60000) { setCaptura({ pantalla: p.nombre, msg: 'La pantalla no respondió en 60 s (¿está en línea?)', esperando: false }); return; }
        await new Promise((res) => setTimeout(res, 3000));
        const e = (await apiClient.post('/rpc/sig_comando_estado', { _id: r.id }, SIG)).data as { estado: string; resultado?: { img?: string; msg?: string } };
        if (e.estado === 'hecho') { setCaptura({ pantalla: p.nombre, img: e.resultado?.img, msg: e.resultado?.img ? undefined : 'La pantalla respondió sin imagen', esperando: false }); return; }
        if (e.estado === 'error') { setCaptura({ pantalla: p.nombre, msg: e.resultado?.msg ?? 'El box reportó un error', esperando: false }); return; }
      }
    } catch (e) { setCaptura(null); toast.error(getErrorMessage(e)); }
  };

  const eliminar = async (p: Pantalla) => {
    try {
      await apiClient.patch(`/rest/signage.pantalla?id=eq.${p.id}`, { deleted_at: new Date().toISOString(), es_activo: false }, SIG);
      invalidar(); toast.success('Pantalla eliminada');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const regenerar = async (p: Pantalla) => {
    try {
      await apiClient.post('/rpc/pantalla_token', { _id: p.id }, SIG);
      invalidar(); toast.success('Token regenerado — la URL anterior dejó de funcionar');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const copiar = (token: string) => { void navigator.clipboard.writeText(urlReproductor(token)); toast.success('URL del reproductor copiada'); };

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      {dlg.ui}
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Monitor className="w-5 h-5" /> Pantallas</h1>
        <span className="text-sm text-muted-foreground">{pantallas.filter((p) => online(p.last_seen)).length}/{pantallas.length} en línea</span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" onClick={() => { setVinc(''); setEdit({ orientacion: 'horizontal', es_activo: true }); }}>
            <Link2 className="w-4 h-4 mr-1" /> Vincular pantalla
          </Button>
          <Button onClick={() => { setVinc(null); setEdit({ orientacion: 'horizontal', es_activo: true }); }}>
            <Plus className="w-4 h-4 mr-1" /> Nueva pantalla
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground border-b bg-muted/30">
              <th className="py-2 px-3 text-right w-10">N°</th>
              <th className="py-2 px-3 text-left">Pantalla</th>
              <th className="py-2 px-3 text-left">Local</th>
              <th className="py-2 px-3 text-left">Plantilla</th>
              <th className="py-2 px-3 text-left">Estado</th>
              <th className="py-2 px-3 text-left">Reproductor</th>
              <th className="py-2 px-3" />
            </tr>
          </thead>
          <tbody>
            {pantallas.map((p, i) => (
              <tr key={p.id} className="border-b last:border-0">
                <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 px-3">{p.nombre} {p.codigo && <span className="text-xs text-muted-foreground">{p.codigo}</span>}</td>
                <td className="py-2 px-3">{p.location_id ? nombreLoc[p.location_id] ?? '—' : '—'}</td>
                <td className="py-2 px-3">{p.plantilla_id ? nombrePla[p.plantilla_id] ?? '—' : '—'}</td>
                <td className="py-2 px-3">
                  <span className={`inline-flex items-center gap-1.5 ${online(p.last_seen) ? 'text-green-600' : 'text-muted-foreground'}`}>
                    <span className={`w-2 h-2 rounded-full ${online(p.last_seen) ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                    {online(p.last_seen) ? 'En línea' : p.last_seen ? `Vista ${new Date(p.last_seen).toLocaleString(LOCALE)}` : 'Nunca conectada'}
                  </span>
                  {p.salud && (
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground" title={p.salud.modelo ? `${p.salud.modelo}${p.salud.app ? ` · Fully ${p.salud.app}` : ''}` : undefined}>
                      {p.salud.kiosk && <span className="rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-1 font-medium">Fully</span>}
                      {p.salud.bateria != null && <span className="inline-flex items-center gap-0.5"><BatteryCharging className={`w-3 h-3 ${p.salud.enchufado ? '' : 'text-amber-500'}`} />{p.salud.bateria}%</span>}
                      {p.salud.wifi != null && <span className="inline-flex items-center gap-0.5"><Wifi className="w-3 h-3" />{p.salud.wifi}</span>}
                      {p.salud.heap_mb != null && <span>{p.salud.heap_mb} MB</span>}
                    </div>
                  )}
                </td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-1">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Copiar URL" onClick={() => copiar(p.token)}><Copy className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="QR del reproductor (escanear para abrir)" onClick={() => setQrDe(p)}><QrCode className="w-4 h-4" /></button>
                    <a className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Abrir reproductor" href={urlReproductor(p.token)} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /></a>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Regenerar token" onClick={() => void regenerar(p)}><RefreshCw className="w-4 h-4" /></button>
                    <span className="w-px h-5 bg-border mx-0.5" />
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Refrescar contenido (remoto)" onClick={() => void comando(p, 'refrescar', 'Refrescar')}><RefreshCcw className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Recargar reproductor (remoto)" onClick={() => void comando(p, 'recargar', 'Recargar')}><RotateCw className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Limpiar caché + recargar (remoto)" onClick={() => void comando(p, 'limpiar_cache', 'Limpiar caché')}><Eraser className="w-4 h-4" /></button>
                    <span className="w-px h-5 bg-border mx-0.5" />
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Capturar pantalla (requiere Fully Kiosk)" onClick={() => void capturar(p)}><Camera className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Reiniciar la app del box (Fully Kiosk)"
                      onClick={async () => { if (await dlg.confirmar(`¿Reiniciar la app del box "${p.nombre}"?`)) void comando(p, 'reiniciar', 'Reinicio de app'); }}><RotateCw className="w-4 h-4 text-amber-600" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="REINICIAR EL EQUIPO (Fully Kiosk PLUS)"
                      onClick={async () => { if (await dlg.confirmar(`¿REINICIAR EL EQUIPO de "${p.nombre}"?`, { detalle: 'La pantalla queda en negro ~1 minuto.', peligro: true })) void comando(p, 'reboot', 'Reboot del equipo'); }}><Power className="w-4 h-4 text-red-500" /></button>
                  </div>
                </td>
                <td className="py-2 px-3 text-right">
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEdit(p)}><Pencil className="w-4 h-4" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => void eliminar(p)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {!pantallas.length && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Aún no hay pantallas. Crea una y abre su URL en el box Android.</td></tr>}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Cada pantalla tiene una <strong>URL única</strong> (su token). Ábrela en el navegador del box en modo kiosko: reproduce
        la plantilla asignada (o la de su programación) con el catálogo del local. El estado “En línea” se basa en el último latido.
        Con <strong>Fully Kiosk Browser</strong> en el box además ves batería/wifi y puedes capturar la pantalla, reiniciar la app o el equipo (ver la Ayuda).
      </p>

      {qrDe && (
        <Modal title={`QR del reproductor — ${qrDe.nombre}`} onClose={() => setQrDe(null)}>
          <div className="space-y-3 text-center">
            <div className="inline-block rounded-lg border bg-white p-4">
              <QRCodeSVG value={urlReproductor(qrDe.token)} size={240} level="M" bgColor="#ffffff" fgColor="#101010" />
            </div>
            <p className="text-xs text-muted-foreground break-all">{urlReproductor(qrDe.token)}</p>
            <p className="text-sm text-muted-foreground">
              Escanéalo con la cámara del celular/tablet que hará de pantalla y se abre el reproductor directo
              (sin teclear nada). Para Android TV box sigue siendo mejor el <b>emparejamiento por código</b> (/signage/pair).
              ⚠ Este QR contiene el token de la pantalla: no lo publiques.
            </p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={() => copiar(qrDe.token)}><Copy className="w-4 h-4 mr-1" /> Copiar URL</Button>
              <Button variant="ghost" onClick={() => setQrDe(null)}>Cerrar</Button>
            </div>
          </div>
        </Modal>
      )}

      {captura && (
        <Modal title={`Captura — ${captura.pantalla}`} onClose={() => setCaptura(null)}>
          <div className="space-y-3">
            {captura.esperando && <p className="text-sm text-muted-foreground py-6 text-center">Esperando al box… (revisa su cola cada 15 s, esto toma ~15-25 s)</p>}
            {captura.img && <img src={captura.img} alt="Captura de la pantalla" className="w-full rounded-md border" />}
            {captura.msg && <p className="text-sm text-amber-700 dark:text-amber-300">{captura.msg}</p>}
            <div className="flex justify-end"><Button variant="ghost" onClick={() => setCaptura(null)}>Cerrar</Button></div>
          </div>
        </Modal>
      )}

      {edit && (
        <Modal title={vinc !== null ? 'Vincular pantalla (por código)' : edit.id ? 'Editar pantalla' : 'Nueva pantalla'} onClose={cerrar}>
          <div className="space-y-3">
            {vinc !== null && (
              <Campo label="Código que muestra la pantalla">
                <input className={`${fieldCls} font-mono tracking-widest text-lg`} inputMode="numeric" maxLength={6} placeholder="000000"
                       value={vinc} onChange={(e) => setVinc(e.target.value.replace(/\D/g, ''))} autoFocus />
              </Campo>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre"><input className={fieldCls} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} autoFocus={vinc === null} /></Campo>
              <Campo label="Código"><input className={fieldCls} value={edit.codigo ?? ''} onChange={(e) => setEdit({ ...edit, codigo: e.target.value })} /></Campo>
              <Campo label="Local">
                <select className={fieldCls} value={edit.location_id ?? ''} onChange={(e) => setEdit({ ...edit, location_id: e.target.value })}>
                  <option value="">— Selecciona —</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Grupo (opcional)">
                <select className={fieldCls} value={edit.grupo_id ?? ''} onChange={(e) => setEdit({ ...edit, grupo_id: e.target.value })}>
                  <option value="">— Sin grupo —</option>
                  {grupos.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Plantilla (fallback)">
                <select className={fieldCls} value={edit.plantilla_id ?? ''} onChange={(e) => setEdit({ ...edit, plantilla_id: e.target.value })}>
                  <option value="">— Selecciona —</option>
                  {plantillas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Tema (override, opcional)">
                <select className={fieldCls} value={edit.tema_id ?? ''} onChange={(e) => setEdit({ ...edit, tema_id: e.target.value })}>
                  <option value="">— El de la plantilla —</option>
                  {temas.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Orientación">
                <select className={fieldCls} value={edit.orientacion ?? 'horizontal'} onChange={(e) => setEdit({ ...edit, orientacion: e.target.value })}>
                  <option value="horizontal">Horizontal</option>
                  <option value="vertical">Vertical</option>
                </select>
              </Campo>
              <Campo label="Resolución (opcional)"><input className={fieldCls} placeholder="1920x1080" value={edit.resolucion ?? ''} onChange={(e) => setEdit({ ...edit, resolucion: e.target.value })} /></Campo>
              {vinc === null && (
                <Campo label="Modo">
                  <select className={fieldCls} value={edit.modo ?? 'playlist'} onChange={(e) => setEdit({ ...edit, modo: e.target.value })}>
                    <option value="playlist">Playlist (cartelería normal)</option>
                    <option value="caja">Pantalla de cliente en caja</option>
                  </select>
                </Campo>
              )}
              {vinc === null && edit.modo === 'caja' && (
                <Campo label="Caja del POS">
                  <select className={fieldCls} value={edit.caja_id ?? ''} onChange={(e) => setEdit({ ...edit, caja_id: e.target.value })}>
                    <option value="">— Cualquier caja del local —</option>
                    {cajas.filter((c) => !edit.location_id || c.location_id === edit.location_id).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </Campo>
              )}
            </div>
            {vinc === null && edit.modo === 'caja' && (
              <p className="text-xs text-muted-foreground">
                Con compra en curso muestra al cliente las líneas, el total, el QR De Una y el vuelto; ociosa reproduce la playlist
                normal (publicidad en el punto de pago). No muestra nombre ni datos del cliente.
              </p>
            )}
            <Switch checked={edit.es_activo ?? true} onChange={(v) => setEdit({ ...edit, es_activo: v })} label="Pantalla activa" />

            {vinc === null && (
              <div className="border rounded-md p-2.5 space-y-2">
                <Switch checked={edit.gadgets != null}
                  onChange={(v) => setEdit({ ...edit, gadgets: v ? { ...GADGETS_DEFAULT } : null })}
                  label="Personalizar gadgets de ESTA pantalla (apagado = hereda del grupo)" />
                {edit.gadgets != null && (
                  <GadgetsEditor value={edit.gadgets} onChange={(g) => setEdit({ ...edit, gadgets: g })} />
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={cerrar}>Cancelar</Button>
              <Button onClick={() => void guardar()} isLoading={guardando}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
